import React, { useState, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { QueryRouteComponentProps } from 'shared/components/QueryRoute';
import ListViewLayout from 'shared/layout/ListViewLayout';
import { ListViewSearchArea } from 'shared/layout';
import {
  Button,
  Input,
  createStyles,
  Theme,
  Select,
  MenuItem,
  OutlinedInput,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { createStyled } from 'shared/hocs';
import * as create from 'shared/components/Table';
import { useQuery } from 'react-apollo';
import { GlobalCircleLoader, ListPage } from 'shared/components';
import { GeneralSearchQueryResult } from 'users/queries';
import { ListPageRenderProps, SortDirection } from 'shared/components/ListPage';
import gql from 'graphql-tag';
import Team from 'fragments/Team';
import User from 'fragments/User';
import { getUsersCsv } from 'shared/csv';
import { Table, TableRow, Pagination } from 'shared/components';
import { saveAs } from 'file-saver';
import MemberListItem from './MemberListItem';
import AddUserToOrganisationModal from './AddUserToOrganisationModal';

interface QueryDefinition {
  teamId?: string;
  search?: string;
  order: SortDirection;
  sort: string;
  perPage: number;
  page: number;
}

interface QueryVars {
  searchText?: string;
  groupId?: string;
  teamId?: string;
  page: number;
  limit: number;
  order: SortDirection;
  sortBy?: string;
}

interface QueryRes {
  users: {
    list: GeneralSearchQueryResult[];
    hasMore: boolean;
  };
}

const GET_TEAMS_QUERY = gql`
  query getTeams($organisationId: String!) {
    getTeams(organisationId: $organisationId) {
      ...TeamBase
    }
  }
  ${Team.fragments.teamBase}
`;

interface TeamType {
  _id: string;
  name: string;
}

interface TeamQueryVars {
  organisationId: string;
}

interface TeamQueryResult {
  getTeams: TeamType[];
}

const GROUP_MEMBER_LIST_QUERY = gql`
  query users(
    $searchText: String
    $groupId: String
    $teamId: String
    $page: Int!
    $limit: Int!
    $order: String
    $sortBy: String
  ) {
    users(
      searchText: $searchText
      groupId: $groupId
      teamId: $teamId
      page: $page
      limit: $limit
      order: $order
      sortBy: $sortBy
    ) {
      list {
        ...UserBase
        organisationAdmin
        profile {
          firstName
          lastName
          jobTitle
        }
        customer {
          id
        }
        group {
          _id
          ownerId
        }
        disabled
        currentTeam
      }
      hasMore
    }
  }
  ${User.fragments.userBase}
`;

const colWidths = ['30%', '12%', '12%', '12%', '12%', '12%', '18%'];

interface SearchQueryOptions {
  search?: string;
  teamId?: string;
}

type Props = QueryRouteComponentProps<QueryDefinition> &
  ListPageRenderProps<SearchQueryOptions>;

const Inner = (props: Props) => {
  const {
    paging,
    searchValues,
    changeRowsPerPage,
    handleChangePage,
    match,
    updateSearch,
    clearSearch,
  } = props;

  const { search: searchText } = searchValues;
  const { order, sortBy, page, perPage: limit } = paging;
  const { groupId } = match.params;

  const [value, setValue] = useState('');
  const [teamId, setTeamId] = useState('');
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);

  useEffect(() => {
    setValue(searchValues.search || '');
    setTeamId(searchValues.teamId || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchText !== undefined && value !== searchText) {
      setValue(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const { data, loading, error, refetch } = useQuery<QueryRes, QueryVars>(
    GROUP_MEMBER_LIST_QUERY,
    {
      variables: {
        searchText,
        teamId,
        groupId,
        page,
        limit,
        order,
        sortBy,
      },
    },
  );

  const {
    data: teamsData,
    loading: teamsLoading,
    error: teamsError,
  } = useQuery<TeamQueryResult, TeamQueryVars>(GET_TEAMS_QUERY, {
    variables: { organisationId: groupId },
    fetchPolicy: 'cache-and-network',
  });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      search();
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.value);
  };

  const search = () => {
    const query: SearchQueryOptions = {};
    if (value || teamId) {
      if (value) query.search = value;
      if (teamId) query.teamId = teamId;
      updateSearch(query);
    } else {
      clearSearch();
    }
  };

  const headers = [
    create.header('Username', 'username'),
    create.header('First Name', 'firstName'),
    create.header('Last Name', 'lastName'),
    create.header('User Type', 'userType'),
    create.header('Team'),
    create.header('Status'),
    create.header('Actions'),
  ];

  if (error) return <span>{error.message}</span>;
  if (teamsError) return <span>{teamsError.message}</span>;
  if (loading || teamsLoading) return <GlobalCircleLoader show />;

  const { users } = data!;
  const { getTeams } = teamsData!;

  const usersListWithTeamNames = users.list.map((user) => {
    const teamId = user.currentTeam || '-';
    const team = getTeams.find((t) => t._id === teamId);
    return {
      ...user,
      currentTeam: team ? team.name : teamId,
    };
  });

  return (
    <Style>
      {({ classes }) => (
        <ListViewLayout>
          <ListViewSearchArea>
            <Input
              fullWidth
              disableUnderline
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              className={classes.searchInput}
              placeholder="Search by last name or username..."
            />

            <Select
              input={
                <OutlinedInput labelWidth={4} className={classes.searchInput} />
              }
              displayEmpty
              value={teamId}
              onChange={(e) => {
                setTeamId(e.target.value as string);
              }}
            >
              <MenuItem value="">
                <em>Filter by team...</em>
              </MenuItem>
              {getTeams.map((x) => (
                <MenuItem key={x._id} value={x._id}>
                  {x.name}
                </MenuItem>
              ))}
            </Select>

            <Button onClick={search} variant="contained" color="primary">
              <SearchIcon />
            </Button>
          </ListViewSearchArea>

          <div>
            <Button
              className={classes.csvBar}
              variant="contained"
              color="secondary"
              onClick={() => {
                const blob = new Blob([getUsersCsv(users.list)], {
                  type: 'text/plain;charset=utf-8',
                });
                saveAs(blob, `org-members-${groupId}-partial.csv`);
              }}
            >
              Download CSV - {users.list.length} Member Results
            </Button>
            <Button
              onClick={() => setAddMemberModalOpen(true)}
              variant="contained"
              color="primary"
            >
              Add member
            </Button>
          </div>
          <hr />
          <Table headers={headers} colWidths={colWidths}>
            {usersListWithTeamNames.map((user) => (
              <TableRow<GeneralSearchQueryResult>
                key={user._id}
                item={user}
                refetch={refetch}
                component={MemberListItem}
              />
            ))}
          </Table>
          <Pagination
            page={page}
            perPage={limit}
            hasMore={users.hasMore}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={changeRowsPerPage}
          />
          <GlobalCircleLoader show={loading} />
          <AddUserToOrganisationModal
            groupId={groupId}
            isOpen={addMemberModalOpen}
            onClose={() => setAddMemberModalOpen(false)}
            onSuccess={() => {
              refetch();
              setAddMemberModalOpen(false);
            }}
          />
        </ListViewLayout>
      )}
    </Style>
  );
};

interface SearchVars {
  search?: string;
  teamId?: string;
}

const sortKeys = ['firstName', 'lastName', 'created', 'balance'];
const MembersList = (props: Props) => {
  const searchKeys: Array<keyof SearchVars> = ['search', 'teamId'];
  return (
    <ListPage<SearchVars>
      sortKeys={sortKeys}
      searchKeys={searchKeys}
      defaultSortBy="created"
    >
      {(query) => <Inner {...query} {...props} />}
    </ListPage>
  );
};

export default MembersList;

const Style = createStyled((theme: Theme) =>
  createStyles({
    searchInput: {
      borderRadius: theme.shape.borderRadius,
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: theme.palette.grey[500],
      padding: theme.spacing.unit,
    },
    csvBar: {
      float: 'right',
    },
  }),
);
